import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const personalQuestions = [
  "how important healthcare is to you",
  "whether you are comfortable learning another language",
  "how flexible your budget really is",
  "whether staying close to family matters",
  "what kind of lifestyle you are hoping to create",
  "what you are willing to compromise on — and what you are not",
];

const comparisonShifts = [
  {
    from: "Which country is ranked #1?",
    to: "Which country best supports the life I want to build?",
  },
  {
    from: "Which country gets mentioned most often?",
    to: "Which tradeoffs am I actually comfortable making?",
  },
];

const renderBulletList = (items, prefix) => (
  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid={`${prefix}-list`}>
    {items.map((item, index) => (
      <li key={item} className="flex gap-3" data-testid={`${prefix}-item-${index + 1}`}>
        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function WhyReadingAboutCountriesIsntTheSameAsChoosingOne() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="why-reading-about-countries-page">
      <SeoArticleSchema
        headline="Why Reading About Countries Isn't the Same as Choosing One"
        description="Research can tell you what a country is like. Learn why choosing where to live abroad requires clarity about your priorities before country comparison begins."
        pathname="/insights/why-reading-about-countries-isnt-the-same-as-choosing-one"
        keywords={[
          "how to choose a country to move abroad",
          "country research vs relocation decision",
          "moving abroad decision clarity",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="overflow-hidden rounded-[2rem] border border-stone-300/80 bg-white/92 shadow-[0_18px_48px_rgba(28,25,23,0.06)]" data-testid="why-reading-hero-section">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr]" data-testid="why-reading-hero-grid">
              <div className="px-7 py-10 sm:px-10" data-testid="why-reading-hero-copy">
                <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="why-reading-hero-accent" />
                <div className="flex flex-wrap items-center gap-3" data-testid="why-reading-hero-meta-row">
                  <p className="rc-section-kicker" data-testid="why-reading-hero-kicker">Decision guide</p>
                  <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900" data-testid="why-reading-hero-badge">Start here</span>
                  <span className="text-sm text-stone-500" data-testid="why-reading-hero-reading-time">4 min read</span>
                </div>
                <h1 className="mt-4 font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="why-reading-page-title">
                  Why Reading About Countries Isn&apos;t the Same as Choosing One
                </h1>
                <div className="mt-6 max-w-3xl space-y-4 text-base leading-8 text-stone-600" data-testid="why-reading-page-intro">
                  <p>We live in a world overflowing with information.</p>
                  <p>If you are thinking about moving abroad, you can spend weeks reading, watching, comparing, and still feel no closer to a decision.</p>
                  <p className="text-stone-800">That is because learning about countries and choosing where to build your life are two different challenges.</p>
                </div>
              </div>

              <div className="relative min-h-[320px] lg:min-h-full" data-testid="why-reading-hero-image-panel">
                <img
                  src="https://images.unsplash.com/photo-1609072730603-1d321d86d8ff?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                  alt="Thoughtful person seated by a window while reading and reflecting"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="why-reading-hero-image"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(28,25,23,0.06)_0%,rgba(28,25,23,0.28)_100%)]" />
              </div>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="why-reading-key-takeaway"
            paragraphs={[
              "Reading about countries can tell you what Portugal, Spain, Mexico, or Panama are like, but it cannot tell you which one best fits your life.",
              "A relocation decision becomes clearer only after you know what should carry the most weight for you: budget, healthcare, residency practicality, family ties, pace of life, language comfort, or some other tradeoff you will actually have to live with.",
            ]}
          />

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="why-reading-information-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="why-reading-information-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="why-reading-information-title">Why can research still leave you unsure?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="why-reading-information-copy">
                <p>Most people do not suffer from a lack of relocation information. They suffer from an abundance of it.</p>
                <p>One source says Portugal. Another says Spain. Someone else swears by Mexico or Panama. Each argument sounds persuasive in isolation.</p>
                <p className="text-stone-800">Country guides are useful for understanding a place. They are not enough to decide whether that place fits you.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="why-reading-personal-questions-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="why-reading-personal-questions-title">What can’t country guides know about you?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="why-reading-personal-questions-copy">
                A country guide cannot know your tolerance for tradeoffs, constraints, or what your next chapter is meant to feel like.
              </p>
              {renderBulletList(personalQuestions, "why-reading-personal-questions")}
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="why-reading-better-questions-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="why-reading-better-questions-title">What question should replace “Which country is best?”</h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="why-reading-question-shift-grid">
              {comparisonShifts.map((shift, index) => (
                <div key={shift.from} className="rounded-[1.5rem] border border-stone-200/80 bg-stone-50/85 p-5" data-testid={`why-reading-question-shift-${index + 1}`}>
                  <p className="text-sm font-medium uppercase tracking-[0.14em] text-stone-500" data-testid={`why-reading-question-shift-from-label-${index + 1}`}>Instead of asking</p>
                  <p className="mt-3 text-lg leading-8 text-stone-900" data-testid={`why-reading-question-shift-from-${index + 1}`}>{shift.from}</p>
                  <p className="mt-5 text-sm font-medium uppercase tracking-[0.14em] text-stone-500" data-testid={`why-reading-question-shift-to-label-${index + 1}`}>Ask</p>
                  <p className="mt-3 text-lg leading-8 text-stone-700" data-testid={`why-reading-question-shift-to-${index + 1}`}>{shift.to}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="why-reading-better-questions-copy">
              <p>There is no perfect country. Affordable countries can require healthcare compromises. Easy residency can come with weaker job options. Strong systems can require more language adjustment.</p>
              <p className="text-stone-800">The goal is not to remove tradeoffs. It is to understand which tradeoffs you can live with.</p>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="why-reading-clarity-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="why-reading-clarity-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="why-reading-clarity-title">Why does clarity need to come before comparison?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="why-reading-clarity-copy">
                <p>Many people begin comparing countries long before they have clarified what matters most.</p>
                <p>That makes every article feel like another opinion instead of a decision aid.</p>
                <p className="text-stone-800">Comparison becomes useful only after you know what your priorities are supposed to be filtering for.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="why-reading-real-decision-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="why-reading-real-decision-title">What are you really deciding when you move abroad?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="why-reading-real-decision-copy">
                <p>Relocating abroad is not only about choosing a country. It is about choosing a future.</p>
                <p>Countries are the setting. Your priorities are what make the setting workable—or not.</p>
                <p className="text-stone-800">That is why the best relocation decisions usually begin with self-awareness, not with a map.</p>
              </div>
            </article>
          </section>

          <ContextualLinkPanel
            kicker="Where to go next"
            title="If this helped you separate research from decision-making, these are the most useful next pages."
            description="The next sensible move is usually to see the core decision question more directly, notice the biggest early mistake, and then narrow the field before country-by-country research takes over again."
            testIdPrefix="why-reading-next-steps"
            items={[
              {
                slug: "decision-guide",
                eyebrow: "Clarify the full question",
                title: "What country should I move to?",
                description: "See the broader framework for judging country fit before the shortlist starts pulling you in different directions.",
                ctaLabel: "Read the decision guide",
                to: "/insights/what-country-should-i-move-to",
              },
              {
                slug: "biggest-mistake",
                eyebrow: "Tighten the mindset",
                title: "The biggest mistake people make when choosing a country",
                description: "See why comparing too early is usually what creates the overwhelm in the first place.",
                ctaLabel: "Read the insight",
                to: "/insights/the-biggest-mistake-people-make-when-choosing-a-country",
              },
              {
                slug: "shortlist",
                eyebrow: "Turn clarity into a filter",
                title: "Build a realistic shortlist",
                description: "Use your priorities to cut broad research down to the countries still worth comparing seriously.",
                ctaLabel: "Open the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="why-reading-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" data-testid="why-reading-cta-accent" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300" data-testid="why-reading-cta-kicker">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50" data-testid="why-reading-cta-title">Ready to move beyond country research and judge fit more clearly?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300" data-testid="why-reading-cta-copy">
              The assessment helps organize your priorities before you compare countries more deeply, so the shortlist starts reflecting your life instead of other people’s opinions.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4" data-testid="why-reading-cta-actions">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="why-reading-cta-button">
                <Link to="/auth">
                  Start your assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}